import React, { useState, useEffect } from "react";

const WORKER_URL = "https://reflex-backend.alfaoir1.workers.dev";
const COLOR = "#7b19bc";

export default function AdminRevivalForms() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetch(`${WORKER_URL}/revival-forms`)
      .then((r) => r.json())
      .then((data) => {
        setSubmissions(Array.isArray(data) ? data : data.submissions || data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const filtered = submissions.filter((s) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.company || "").toLowerCase().includes(q) ||
      (s.phone || "").toLowerCase().includes(q)
    );
  });

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerInner}>
          <div>
            <p style={s.label}>Admin Panel</p>
            <h1 style={s.title}>Revival Form Submissions</h1>
            <p style={s.sub}>Reflex Revival™ — $297/month signups</p>
          </div>
          <div style={s.meta}>
            <div style={s.badge}>
              <span style={{ ...s.dot, background: COLOR }} />
              {loading ? "Loading…" : `${filtered.length} submission${filtered.length !== 1 ? "s" : ""}`}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={s.content}>

        {/* Search */}
        <div style={s.toolbar}>
          <div style={s.searchWrap}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              style={s.search}
              placeholder="Search by name, email, company, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* States */}
        {loading && (
          <div style={s.state}>
            <div style={s.spinner} />
            <p style={s.stateText}>Loading submissions…</p>
          </div>
        )}
        {error && (
          <div style={s.errorBox}>
            <strong>Failed to load:</strong> {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div style={s.state}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>🔮</p>
            <p style={s.stateText}>{search ? "No results match your search." : "No submissions yet."}</p>
          </div>
        )}

        {/* Cards */}
        {!loading && !error && filtered.length > 0 && (
          <div style={s.list}>
            {filtered.map((sub, i) => {
              const id = sub._id || sub.id || i;
              const isOpen = expanded === id;
              return (
                <div
                  key={id}
                  style={{
                    ...s.card,
                    borderColor: isOpen ? COLOR : "#e5e7eb",
                    boxShadow: isOpen ? `0 0 0 1px ${COLOR}22, 0 4px 16px ${COLOR}11` : "0 1px 2px rgba(0,0,0,0.04)",
                  }}
                >
                  {/* Card Header */}
                  <div
                    style={s.cardHead}
                    onClick={() => setExpanded(isOpen ? null : id)}
                  >
                    <div style={s.avatarWrap}>
                      <div style={{ ...s.avatar, background: `${COLOR}18`, color: COLOR }}>
                        {(sub.name || "?")[0].toUpperCase()}
                      </div>
                    </div>
                    <div style={s.cardInfo}>
                      <div style={s.cardName}>{sub.name || "—"}</div>
                      <div style={s.cardMeta}>
                        {sub.email && <span>{sub.email}</span>}
                        {sub.phone && <><span style={s.sep}>·</span><span>{sub.phone}</span></>}
                        {sub.company && <><span style={s.sep}>·</span><span>{sub.company}</span></>}
                      </div>
                    </div>
                    <div style={s.cardRight}>
                      <span style={s.pkgBadge}>{sub.package || "Reflex Revival™"}</span>
                      <span style={s.priceBadge}>{sub.packagePrice || "$297/mo"}</span>
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isOpen && (
                    <div style={s.detail}>
                      <div style={s.detailDivider} />
                      <div style={s.detailGrid}>
                        <Field label="Full Name" value={sub.name} />
                        <Field label="Email" value={sub.email} link={`mailto:${sub.email}`} />
                        <Field label="Phone" value={sub.phone} link={`tel:${sub.phone}`} />
                        <Field label="Company" value={sub.company} />
                        <Field label="Website" value={sub.website} link={sub.website?.startsWith("http") ? sub.website : sub.website ? `https://${sub.website}` : null} />
                        <Field label="Package" value={sub.package} />
                        <Field label="Price" value={sub.packagePrice} accent={COLOR} />
                        <Field label="Submitted" value={fmt(sub.submittedAt)} />
                      </div>
                      {sub.note && (
                        <div style={s.noteBox}>
                          <p style={s.noteLabel}>Business Note</p>
                          <p style={s.noteText}>{sub.note}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #9ca3af; }
        input:focus { outline: none; }
      `}</style>
    </div>
  );
}

function Field({ label, value, link, accent }) {
  return (
    <div style={f.wrap}>
      <p style={f.label}>{label}</p>
      {link
        ? <a href={link} target="_blank" rel="noreferrer" style={{ ...f.value, color: "#7b19bc", textDecoration: "none" }}>{value || "—"}</a>
        : <p style={{ ...f.value, ...(accent ? { color: accent, fontWeight: 700 } : {}) }}>{value || "—"}</p>
      }
    </div>
  );
}

const f = {
  wrap: { display: "flex", flexDirection: "column", gap: 3 },
  label: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9ca3af" },
  value: { fontSize: 13.5, color: "#111827", margin: 0 },
};

const s = {
  page: {
    background: "#f7f7fa", minHeight: "100vh", color: "#0a0a0a",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
  },
  header: { background: "#ffffff", padding: "60px 40px 40px", borderBottom: "1px solid #ece9f3" },
  headerInner: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", maxWidth: "900px", margin: "0 auto" },
  label: { fontSize: 12, color: COLOR, textTransform: "uppercase", letterSpacing: "3px", margin: "0 0 10px", fontWeight: 700 },
  title: { fontSize: 36, fontWeight: 800, margin: "0 0 8px", color: "#0a0a0a" },
  sub: { fontSize: 15, color: "#6b7280", margin: 0 },
  meta: { display: "flex", alignItems: "center", gap: 12, marginTop: 8 },
  badge: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#f3f4f6", border: "1px solid #e5e7eb",
    borderRadius: 20, padding: "6px 14px",
    fontSize: 12, fontWeight: 600, color: "#374151",
  },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  content: { maxWidth: "900px", margin: "0 auto", padding: "40px 20px 80px" },
  toolbar: { marginBottom: 24 },
  searchWrap: {
    display: "flex", alignItems: "center", gap: 10,
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "10px 16px", maxWidth: 420,
  },
  search: {
    border: "none", background: "transparent", fontSize: 14, color: "#111827",
    width: "100%", fontFamily: "inherit",
  },
  state: { textAlign: "center", padding: "80px 0" },
  stateText: { fontSize: 15, color: "#9ca3af" },
  spinner: {
    width: 28, height: 28, borderRadius: "50%", margin: "0 auto 16px",
    border: "2.5px solid #e5e7eb", borderTopColor: COLOR,
    animation: "spin 0.8s linear infinite",
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 10, padding: "16px 20px", color: "#dc2626", fontSize: 14,
  },
  list: { display: "flex", flexDirection: "column", gap: 12 },
  card: {
    background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 16,
    overflow: "hidden", transition: "border-color 0.2s, box-shadow 0.2s",
  },
  cardHead: {
    display: "flex", alignItems: "center", gap: 16,
    padding: "20px 24px", cursor: "pointer",
  },
  avatarWrap: { flexShrink: 0 },
  avatar: {
    width: 40, height: 40, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, fontWeight: 800,
  },
  cardInfo: { flex: 1, minWidth: 0 },
  cardName: { fontSize: 15, fontWeight: 700, color: "#0a0a0a", marginBottom: 3 },
  cardMeta: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 13, color: "#6b7280" },
  sep: { color: "#d1d5db" },
  cardRight: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
  pkgBadge: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
    background: `${COLOR}10`, color: COLOR, borderRadius: 6, padding: "4px 9px",
  },
  priceBadge: {
    fontSize: 11, fontWeight: 700,
    background: "#f0fdf4", color: "#15803d", borderRadius: 6, padding: "4px 9px",
  },
  detailDivider: { height: 1, background: "#f3f4f6", margin: "0 24px" },
  detail: { padding: "20px 24px 24px" },
  detailGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "16px 24px",
  },
  noteBox: {
    marginTop: 20, background: "#f9fafb", border: "1px solid #e5e7eb",
    borderRadius: 10, padding: "14px 16px",
  },
  noteLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9ca3af", marginBottom: 6 },
  noteText: { fontSize: 13.5, color: "#374151", lineHeight: 1.65, margin: 0 },
};